import { useState } from 'react';
import { JSONContent } from '@tiptap/core';

import { SlideshowSlide, ViewProps } from './slideshow.constants';
import { PagesPreviewModal, PagesPreviewExpand } from '../shared/pages-preview';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useCommonSlideshow } from './slideshow.hooks';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import { ComponentAspectRatio } from '../shared/component-height/component-dimensions';
import {
  ActivityBuilderDualNav,
  ActivityPresentNextControl,
  ActivityPresentPrevControl,
} from 'approot/activity-builder/present/activity-present-nav-controls';
import { ComponentImage } from '../component-image';
import { ImageViewFill } from '../image/image-view';
import { ComponentCaption } from '../shared/component-caption/component-caption';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import IconDropdown from 'approot/shared/icons/icon-dropdown';
import {
  PlayableComponentButton,
  PlayableComponentButtonText,
} from '../shared/playable-component.style';
import * as Styled from './slideshow-view.style';

const SlideshowModal = ({
  slideshowImages,
  showCaption,
  selected,
  isStudent,
  onClose,
}: {
  slideshowImages: SlideshowSlide[] | null;
  showCaption: boolean;
  selected: number;
  isStudent?: boolean;
  onClose: () => void;
}) => {
  const {
    slides,
    imageDimensions,
    index,
    isLoading,
    getPrev,
    getNext,
    setContainerElement,
  } = useCommonSlideshow({
    slideshowImages,
    selectedIndex: selected,
    captureKeypress: true,
  });

  const slide: SlideshowSlide | undefined = slides[index];

  return (
    <PagesPreviewModal
      name="Image Slideshow"
      hasCaption
      close={onClose}
      content={
        <>
          <Styled.SlideshowContainer ref={setContainerElement}>
            {slides.length > 1 && (
              <>
                {isStudent ? (
                  <>
                    <ActivityPresentPrevControl
                      isStart={index === 0}
                      ariaLabel="image"
                      ariaNavigationLabel="Image pagination"
                      handlePrev={getPrev}
                    />
                    <ActivityPresentNextControl
                      isEnd={index === slides.length - 1}
                      ariaLabel="image"
                      ariaNavigationLabel="Image pagination"
                      handleNext={getNext}
                    />
                  </>
                ) : (
                  <ActivityBuilderDualNav
                    isEnd={index === slides.length - 1}
                    isStart={index === 0}
                    ariaLabel="image"
                    ariaNavigationLabel="Image pagination"
                    handleNext={getNext}
                    handlePrevious={getPrev}
                  />
                )}
              </>
            )}
            <Styled.SlideshowWrapper>
              <Styled.SlideshowPages $isLoading={isLoading}>
                {!isLoading && (
                  <ComponentImage
                    images={slide?.properties.imageUrls || []}
                    altText={slide?.properties.altText || ''}
                    imageDimensions={imageDimensions}
                  />
                )}
              </Styled.SlideshowPages>
              {showCaption && (
                <SlideshowCaption
                  id={slide.id}
                  caption={slide.properties.caption}
                  isSmall={false}
                />
              )}
            </Styled.SlideshowWrapper>
            <Styled.SlideshowPageControls>
              <span>{`Page ${index + 1} of ${slides.length}`}</span>
            </Styled.SlideshowPageControls>
          </Styled.SlideshowContainer>
        </>
      }
    />
  );
};

const SlideshowView = ({
  slideshowImages,
  properties,
  isStudent,
  onComponentExpand,
}: ViewProps & {
  isStudent?: boolean;
}) => {
  const { slides, imageDimensions, index, isLoading, setContainerElement } =
    useCommonSlideshow({
      slideshowImages,
      selectedIndex: 0,
      captureKeypress: false,
    });

  const { openModal, closeModal, isVisible } = useSimpleModalState();

  const slide: SlideshowSlide | undefined = slides[index];

  return (
    <ComponentAspectRatio dimensions={properties.dimensionData}>
      <ComponentAbsoluteWrapper $centred={false}>
        {isVisible && (
          <SlideshowModal
            slideshowImages={slideshowImages}
            selected={index}
            isStudent={isStudent}
            showCaption={!!properties.showSlideCaptionOnLesson}
            onClose={closeModal}
          />
        )}
        <Styled.SlideshowContainer ref={setContainerElement}>
          <Styled.SlideshowWrapper>
            <Styled.SlideshowPages $isLoading={isLoading}>
              {slide && !isLoading && (
                <>
                  {!slide.properties.cropData ? (
                    <ComponentImage
                      images={slide.properties.imageUrls || []}
                      altText={slide.properties.altText || ''}
                      imageDimensions={imageDimensions}
                    />
                  ) : (
                    <ImageViewFill
                      imageUrls={slide.properties.imageUrls || []}
                      altText={slide.properties.altText}
                    />
                  )}
                </>
              )}
            </Styled.SlideshowPages>
          </Styled.SlideshowWrapper>
          <PagesPreviewExpand
            ariaLabel="Click to expand slideshow images"
            onClick={() => {
              onComponentExpand?.();
              openModal();
            }}
          />

          <Styled.SlideshowPageControls>
            <span>
              {slides.length === 0
                ? 'No images in slideshow'
                : `Page ${index + 1} of ${slides.length}`}
            </span>
          </Styled.SlideshowPageControls>
        </Styled.SlideshowContainer>
        <PlayableComponentButton
          type="button"
          aria-label="Click to start viewing the slideshow"
          onClick={() => {
            if (slides.length > 0) {
              onComponentExpand?.();
              openModal();
            }
          }}
          data-selector="btn-start"
        >
          <PlayableComponentButtonText>Start</PlayableComponentButtonText>
        </PlayableComponentButton>
      </ComponentAbsoluteWrapper>
    </ComponentAspectRatio>
  );
};

export default SlideshowView;

const SlideshowCaption = ({
  id,
  caption,
  isSmall,
}: {
  id: string;
  caption?: JSONContent;
  isSmall: boolean;
}) => {
  const [isCaptionActive, setIsCaptionActive] = useState<boolean>(true);

  return (
    <>
      {!isRichtextEmpty(caption) && (
        <>
          {isCaptionActive ? (
            <Styled.SlideshowCaption>
              <Styled.SlideshowCaptionButton
                type="button"
                aria-label="Hide text"
                onClick={() => setIsCaptionActive(false)}
              >
                <IconDropdown width={12} />
              </Styled.SlideshowCaptionButton>
              <ComponentCaption
                id={id}
                caption={caption}
                showCaption
                isSmall={isSmall}
              />
            </Styled.SlideshowCaption>
          ) : (
            <Styled.SlideshowCaptionButtonShow
              type="button"
              onClick={() => setIsCaptionActive(true)}
            >
              Show Text
            </Styled.SlideshowCaptionButtonShow>
          )}
        </>
      )}
    </>
  );
};
